import React, { useState } from 'react';
import { FormikProps } from 'formik';
import { TFunction } from 'react-i18next';
import classNameBind from 'classnames/bind';
import {
  Form,
  Stack,
  TextFieldControl,
  Text,
  Link,
  ModalView,
  withTranslation,
  Information,
} from '@piwikpro/ui-components';
import { AuthSettings } from '@piwikpro/auth-crate';
import { FormValidator } from '@piwikpro/form-crate';
import TitleComponent from '@piwikpro/title-crate/Title';
import { withInject } from '@piwikpro/platform';
import { VerificationCodeInputControl } from '../TwoFactorConfigureOverlay/components/VerificationCodeInput';
import styles from './TwoFactorDisable.module.css';

const bindClasses = classNameBind.bind(styles);

interface IFormValues {
  code: string | number
  informationError?: string
}

interface InjectedProps {
  authService: AuthSettings
  validation: FormValidator
  Title: typeof TitleComponent
}

interface Props extends InjectedProps {
  onClose: ({ setupCompleted }?: { setupCompleted: boolean }) => void
  t: TFunction
}

const TwoFactorDisable: React.FC<Props> = ({
  authService,
  onClose,
  validation,
  Title,
  t,
}) => {
  const [backupCodesViewVisible, setBackupCodesViewVisible] = useState(false);
  return (
    <Title part={t('security.confirmCurrentModal.title')} subset>
      <div className={bindClasses('wrapper')}>
        <Form
          id="two-factor-disable-form"
          validationSchema={validation.createValidationSchema(
            () => {
              const lengthRuleMsg = t('security.totpOrBackupCode.validation', { count: backupCodesViewVisible ? 8 : 6 });

              return {
                code: validation
                  .getYup()
                  .string()
                  .min(backupCodesViewVisible ? 8 : 6, lengthRuleMsg)
                  .max(backupCodesViewVisible ? 8 : 6, lengthRuleMsg),
              };
            },
          )}
          initialValues={{
            code: '',
          }}
          onSubmit={async (values: IFormValues, api: FormikProps<IFormValues>) => {
            try {
              await authService.delete2faForCurrentUser({ code: String(values.code) });
              onClose();
            } catch (err: any) {
              if (err.name === 'InvalidRequestError') {
                api.setErrors({ informationError: t('security.invalidAuthCode.info') });
              }
            } finally {
              api.setFieldValue('code', '', false);
              api.setSubmitting(false);
            }
          }}
          render={({
            isSubmitting,
            resetForm,
            setFieldValue,
            errors,
          }: FormikProps<IFormValues>) => (
            <ModalView
              title={t('security.confirmCurrentModal.title')}
              actions={(
                <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
                  <Form.Cancel onClick={onClose} />
                  <Form.Submit text={t('security.turnOff2faModal.turnOffBtn.text')} />
                </Stack>
              )}
            >
              <Stack vertical>
                <Stack.Item>
                  <Text as="p">
                    {t('security.turnOff2faModal.description.line1')}
                  </Text>
                </Stack.Item>
                <Stack.Item previousSpacing="narrow">
                  <Text as="p">
                    {t('security.turnOff2faModal.description.line2')}
                  </Text>
                </Stack.Item>
                <Stack.Item previousSpacing="narrow">
                  <Text as="p">
                    {backupCodesViewVisible
                      ? t('security.turnOff2faModal.backupCode.info')
                      : t('security.turnOff2faModal.commonCode.info')
                    }
                  </Text>
                </Stack.Item>
                {errors.informationError && (
                  <Stack.Item fill>
                    <Information type="error">
                      {errors.informationError}
                    </Information>
                  </Stack.Item>
                )}
                <Stack.Item fill>
                  {backupCodesViewVisible
                    ? (
                      <TextFieldControl
                        name="code"
                        type="tel"
                        onChange={(e) => {
                          const numericValue = (e.target.value as string).replace(/[^0-9]/g, '');
                          setFieldValue('code', numericValue);
                        }}
                        placeholder={t('security.confirmCurrentModal.backupCode.placeholder')}
                        autoFocus
                      />
                    ) : (
                      <VerificationCodeInputControl
                        name="code"
                        backupCodesLinkText={t('security.confirmCurrentModal.commonCode.link.text')}
                        onBackupCodesLinkClick={() => {
                          resetForm();
                          setBackupCodesViewVisible(true);
                        }}
                        withRightSideSpinner={false}
                      />
                    )}
                </Stack.Item>
                {backupCodesViewVisible && (
                  <Stack.Item fill>
                    <div
                      className={bindClasses('link', { isSubmitting })}
                    >
                      <Link
                        size="small"
                        onClick={() => {
                          resetForm();
                          setBackupCodesViewVisible(false);
                        }}
                      >
                        {t('security.confirmCurrentModal.backupCode.link.text')}
                      </Link>
                    </div>
                  </Stack.Item>
                )}
              </Stack>
            </ModalView>
          )}
        />
      </div>
    </Title>
  );
};

export default withInject<InjectedProps>({
  authService: 'AuthCrate.authSettings',
  validation: 'FormCrate.validation',
  Title: 'TitleCrate.components.Title',
})(withTranslation('profile')(TwoFactorDisable));
